/**
 * Utilities for managing state persistence across the application
 * Provides standardized methods for reading and writing state
 * with validation and stale data prevention
 */

// Constants for state keys
export const STATE_KEYS = {
  EDIT_TEMPLATE: 'editDocStepDone',
  EDIT_STEP: 'editStepDone',
  DEPLOY_STEP: 'deployStepDone',
  EMBED_STEP: 'embedStepDone',
  FUND_CONTRACT: 'fundToContractStepDone',
  ENABLE_CLAIMING: 'enableClaimingStepDone',
  WITHDRAW_TOKENS: 'withdrawPayTokensStepDone',
};

/**
 * Interface for storing state with metadata
 */
export interface StateData {
  value: any;
  timestamp: number;
  version: number;
}

/**
 * Get a state value for a specific project
 * @param key The state key
 * @param projectId The project ID
 * @param useSession Whether to use sessionStorage (true) or localStorage (false)
 * @param maxAge Maximum age of the data in milliseconds before considering it stale
 * @returns The state value or null if not found or stale
 */
export const getStateValue = (
  key: string,
  projectId: string,
  useSession = true,
  maxAge?: number
): any => {
  const storage = useSession ? sessionStorage : localStorage;
  const fullKey = `${key}-${projectId}`;
  const data = storage.getItem(fullKey);
  
  if (!data) {
    // Check the other storage as fallback
    const altStorage = useSession ? localStorage : sessionStorage;
    const altData = altStorage.getItem(fullKey);
    
    if (!altData) return null;
    
    try {
      // If found in alternate storage, parse and validate it
      const parsed = JSON.parse(altData);
      
      // If it's a legacy boolean value, return it directly
      if (typeof parsed === 'boolean') return parsed;
      
      // For proper state objects, validate timestamp if maxAge provided
      if (typeof parsed === 'object' && parsed.timestamp) {
        if (maxAge && (Date.now() - parsed.timestamp > maxAge)) {
          // Data is stale
          return null;
        }
        return parsed.value;
      }
      
      return parsed;
    } catch (e) {
      // If it's a string like 'true', convert to boolean
      if (altData === 'true') return true;
      if (altData === 'false') return false;
      return altData;
    }
  }
  
  try {
    // Parse data from primary storage
    const parsed = JSON.parse(data);
    
    // If it's a legacy boolean value, return it directly
    if (typeof parsed === 'boolean') return parsed;
    
    // For proper state objects, validate timestamp if maxAge provided
    if (typeof parsed === 'object' && parsed.timestamp) {
      if (maxAge && (Date.now() - parsed.timestamp > maxAge)) {
        // Data is stale
        return null;
      }
      return parsed.value;
    }
    
    return parsed;
  } catch (e) {
    // If it's a string like 'true', convert to boolean
    if (data === 'true') return true;
    if (data === 'false') return false;
    return data;
  }
};

/**
 * Set a state value for a specific project
 * @param key The state key
 * @param projectId The project ID
 * @param value The value to store
 * @param useSession Whether to use sessionStorage (true) or localStorage (false)
 */
export const setStateValue = (
  key: string,
  projectId: string,
  value: any,
  useSession = true
): void => {
  const storage = useSession ? sessionStorage : localStorage;
  const fullKey = `${key}-${projectId}`;
  
  // Store with metadata
  const stateData: StateData = {
    value,
    timestamp: Date.now(),
    version: 1, // For future compatibility
  };
  
  storage.setItem(fullKey, JSON.stringify(stateData));
  
  // For backward compatibility, also set in localStorage
  if (useSession) {
    localStorage.setItem(fullKey, JSON.stringify(stateData));
  }
};

/**
 * Clear all state values for a specific project
 * @param projectId The project ID 
 */
export const clearProjectState = (projectId: string): void => {
  const keys = Object.values(STATE_KEYS);
  
  keys.forEach(key => {
    const fullKey = `${key}-${projectId}`;
    sessionStorage.removeItem(fullKey);
    localStorage.removeItem(fullKey);
  });
};

/**
 * Migrate legacy state to new format
 * @param projectId The project ID
 */
export const migrateProjectState = (projectId: string): void => {
  const keys = Object.values(STATE_KEYS);
  
  keys.forEach(key => {
    const fullKey = `${key}-${projectId}`;
    const localValue = localStorage.getItem(fullKey);
    
    if (localValue && (localValue === 'true' || localValue === 'false')) {
      const boolValue = localValue === 'true';
      setStateValue(key, projectId, boolValue, true);
    }
  });
};